import React, { useState, useEffect } from 'react';
import { getScenarioHistory, deleteScenarioHistory, compareScenarios } from '../api';
import {
  ClockCounterClockwise, Trash, GitDiff, X, SpinnerGap,
  FloppyDisk, CaretRight
} from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';

export default function ScenarioHistory({ industry }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data } = await getScenarioHistory(industry);
      setHistory(data.scenarios || []);
    } catch (err) {
      console.error('Failed to load scenario history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteScenarioHistory(id);
      setHistory(prev => prev.filter(s => s._id !== id));
      setSelected(prev => prev.filter(sid => sid !== id));
    } catch (err) {
      console.error('Failed to delete scenario:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const handleCompare = async () => {
    if (selected.length < 2) return;
    setComparing(true);
    try {
      const { data } = await compareScenarios(selected);
      setComparison(data.scenarios || []);
    } catch (err) {
      console.error('Compare failed:', err);
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="scenario-history">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={20} weight="duotone" className="text-primary" />
          <h3 className="font-heading text-base font-semibold text-text-primary">Saved Scenarios</h3>
          <span className="text-xs font-body text-tertiary">({history.length})</span>
        </div>
        {selected.length >= 2 && (
          <button
            data-testid="compare-scenarios-button"
            onClick={handleCompare}
            disabled={comparing}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-heading text-xs font-semibold hover:bg-[#2B3523] transition-all disabled:opacity-50"
          >
            {comparing ? <SpinnerGap size={14} className="animate-spin" /> : <GitDiff size={14} />}
            Compare ({selected.length})
          </button>
        )}
      </div>

      {/* Comparison View */}
      {comparison && (
        <div className="bg-surface border-2 border-primary/20 rounded-lg p-5 animate-fade-in" data-testid="scenario-comparison">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading text-base font-bold text-text-primary flex items-center gap-2">
              <GitDiff size={18} className="text-primary" />
              Scenario Comparison
            </h4>
            <button
              data-testid="close-comparison"
              onClick={() => setComparison(null)}
              className="p-1.5 rounded-lg hover:bg-background transition-colors"
            >
              <X size={16} className="text-text-secondary" />
            </button>
          </div>

          {/* Parameter comparison table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-body">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 font-semibold text-tertiary uppercase tracking-wider">Metric</th>
                  {comparison.map((s, i) => (
                    <th key={i} className="text-left py-2 px-3 font-semibold text-text-primary min-w-[160px]">
                      <span className="block text-sm">{s.title}</span>
                      <span className="block text-tertiary font-normal mt-0.5">{s.site}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Parameter changes */}
                {Object.keys(comparison[0]?.parameter_changes || {}).map(key => (
                  <tr key={key} className="border-b border-border/50">
                    <td className="py-2 pr-4 text-text-secondary capitalize">{key.replace(/_/g, ' ')}</td>
                    {comparison.map((s, i) => (
                      <td key={i} className="py-2 px-3 font-medium text-text-primary">
                        {s.parameter_changes?.[key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Projections */}
                {Object.keys(comparison[0]?.projections || {}).map(key => (
                  <tr key={`proj-${key}`} className="border-b border-border/50 bg-background/50">
                    <td className="py-2 pr-4 text-secondary font-medium capitalize">{key.replace(/_/g, ' ')} (projected)</td>
                    {comparison.map((s, i) => {
                      const proj = s.projections?.[key];
                      return (
                        <td key={i} className="py-2 px-3">
                          {proj ? (
                            <div>
                              <span className="font-medium text-text-primary">{proj.projected}</span>
                              <span className={`ml-2 text-[10px] font-semibold ${proj.change_pct < 0 ? 'text-success' : 'text-alert-critical'}`}>
                                {proj.change_pct > 0 ? '+' : ''}{proj.change_pct}%
                              </span>
                            </div>
                          ) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* AI Analysis comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {comparison.map((s, i) => (
              <div key={i} className="p-3 bg-background rounded-lg border border-border">
                <p className="text-xs font-body font-semibold text-primary uppercase tracking-wider mb-2">{s.title} — Analysis</p>
                <div className="prose prose-sm max-w-none text-xs font-body text-text-primary leading-relaxed max-h-48 overflow-y-auto">
                  <ReactMarkdown>{s.analysis?.substring(0, 600) + (s.analysis?.length > 600 ? '...' : '')}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History list */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-surface border border-border rounded-lg animate-pulse" />)}
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-8 bg-surface border border-border rounded-lg">
          <FloppyDisk size={36} weight="duotone" className="text-border mx-auto mb-2" />
          <p className="font-body text-sm text-text-secondary">No saved scenarios yet</p>
          <p className="font-body text-xs text-tertiary mt-1">Run a what-if analysis and save the results to see them here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {history.map((scenario) => (
            <div
              key={scenario._id}
              className={`bg-surface border rounded-lg transition-all duration-200 ${
                selected.includes(scenario._id)
                  ? 'border-primary/40 ring-1 ring-primary/20'
                  : 'border-border hover:border-tertiary/40'
              }`}
            >
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <button
                  data-testid={`select-scenario-${scenario._id}`}
                  onClick={() => toggleSelect(scenario._id)}
                  className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                    selected.includes(scenario._id)
                      ? 'bg-primary border-primary'
                      : 'border-border hover:border-tertiary'
                  }`}
                >
                  {selected.includes(scenario._id) && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>

                {/* Content */}
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === scenario._id ? null : scenario._id)}
                >
                  <div className="flex items-center gap-2">
                    <p className="font-body text-sm font-medium text-text-primary truncate">{scenario.title}</p>
                    <CaretRight
                      size={12}
                      className={`text-tertiary transition-transform ${expandedId === scenario._id ? 'rotate-90' : ''}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] font-body text-tertiary">{scenario.site}</span>
                    <span className="text-border">|</span>
                    <span className="text-[10px] font-body text-tertiary">{scenario.saved_by}</span>
                    <span className="text-border">|</span>
                    <span className="text-[10px] font-body text-tertiary">
                      {new Date(scenario.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Delete */}
                <button
                  data-testid={`delete-scenario-${scenario._id}`}
                  onClick={() => handleDelete(scenario._id)}
                  className="p-1.5 rounded-lg hover:bg-alert-critical/10 transition-colors"
                >
                  <Trash size={14} className="text-text-secondary hover:text-alert-critical" />
                </button>
              </div>

              {/* Expanded details */}
              {expandedId === scenario._id && (
                <div className="px-3 pb-3 pt-0 border-t border-border/50 animate-fade-in">
                  {/* Parameters */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {Object.entries(scenario.parameter_changes || {}).map(([k, v]) => (
                      <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded-md text-[10px] font-body">
                        <span className="text-tertiary capitalize">{k.replace(/_/g, ' ')}:</span>
                        <span className="font-medium text-text-primary">{v}</span>
                      </span>
                    ))}
                  </div>

                  {/* Projections */}
                  {scenario.projections && Object.keys(scenario.projections).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                      {Object.entries(scenario.projections).slice(0, 4).map(([key, val]) => (
                        <div key={key} className="p-2 bg-background rounded-lg">
                          <p className="text-[10px] font-body text-tertiary capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="font-heading text-sm font-bold text-text-primary">{val.projected}</p>
                          <span className={`text-[10px] font-body font-medium ${val.change_pct < 0 ? 'text-success' : 'text-alert-critical'}`}>
                            {val.change_pct > 0 ? '+' : ''}{val.change_pct}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Analysis preview */}
                  <div className="mt-3 p-3 bg-background rounded-lg border border-border max-h-40 overflow-y-auto">
                    <div className="prose prose-sm max-w-none text-xs font-body text-text-primary">
                      <ReactMarkdown>{scenario.analysis?.substring(0, 400) + (scenario.analysis?.length > 400 ? '...' : '')}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selected.length > 0 && selected.length < 2 && (
        <p className="text-xs font-body text-tertiary text-center">Select at least 2 scenarios to compare</p>
      )}
    </div>
  );
}
